import { mapState, mapActions } from 'vuex';
import moment from 'moment';
import debounce from 'lodash/debounce';

export default {
  name: 'UserManagementMitra',
  
  data() {
    return {
      imageBaseUrl: process.env.VUE_APP_API_URL_UGEMS_UNIV,
      columns: [
        {
          label: "No",
          field: "no",
          thClass: "table-header text-muted font-weight-bold",
          tdClass: "color-secondary align-middle",
          width: "5%",
          sortable: false
        },
        {
          label: 'iSafe Number',
          field: 'isafe_no',
          sortable: true
        },
        {
          label: 'Nama',
          field: 'fullname',
          sortable: true
        },
        {
          label: 'NIK',
          field: 'nik',
          sortable: true
        },
        {
          label: 'Perusahaan',
          field: 'company',
          sortable: true
        },
        {
          label: 'Role',
          field: 'partner_role',
          sortable: false
        },
        {
          label: 'Action',
          field: 'action',
          sortable: false,
          tdClass: 'text-center'
        }
      ],
      body: {
        page: 1,
        limit: 10
      },
      bodyAdd: {
        nik: '',
        isafe_no: '',
        fullname: '',
        email: '',
        phone: '',
        dob: '',
        doh: '',
        company: '',
        position_title: '',
        id_position: null,
        id_department: null,
        id_company: null,
        id_sub_company: null,
        photo_path: '',
        partner_role: ''
      },
      bodyUpdate: {
        id: null,
        nik: '',
        isafe_no: '',
        full_name: '',
        email: '',
        contact: '',
        date_of_birth: '',
        date_of_hire: '',
        company: '',
        jabatan: '',
        photo_path: '',
        partner_role: ''
      },
      selectedUser: null,
      userOptions: [],
      isSearchingUser: false,
      roleOptions: [
        { label: 'User', value: 'user' },
        { label: 'Admin Mitra', value: 'adminmitra' },
        { label: 'Admin PJO', value: 'adminpjo'},
      ],
      filters: {
        search: '',
        company: null
      },
      partnerRole: {
        user: 'User',
        adminmitra: 'Admin Mitra',
        adminpjo: 'Admin PJO',
      }
    };
  },

  computed: {
    ...mapState('userManagementMitra', ['isLoading', 'userMitraData', 'totalRecords', 'companies'])
  },

  async mounted() {
    await this.loadCompaniesList();
    await this.loadUserMitraData();
  },

  created() {
    // create a debounced fetcher so rapid filter changes don't overload the API
    this.debouncedFetchFilters = debounce(() => {
      this.body.page = 1;
      this.applyFiltersToBody();
      this.loadUserMitraData();
    }, 500);
  },

  watch: {
    // whenever any filter changes, trigger the debounced API call
    filters: {
      handler() {
        if (this.debouncedFetchFilters) this.debouncedFetchFilters();
      },
      deep: true
    }
  },

  methods: {
    ...mapActions('userManagementMitra', [
      'getUserMitraList',
      'searchUserByNik',
      'getUserDetailByNik',
      'createUserMitra',
      'updateUserMitra',
      'deleteUserMitra',
      'getCompaniesList'
    ]),

    // Load companies list
    async loadCompaniesList() {
      try {
        await this.getCompaniesList();
        console.log('Companies loaded');
      } catch (error) {
        console.error('Load companies error:', error);
      }
    },

    // Load user mitra data
    async loadUserMitraData() {
      try {
        await this.getUserMitraList(this.body);
        console.log('User mitra data loaded from server-side pagination');
      } catch (error) {
        console.error('Load user data error:', error);
        this.$swal('Error', 'Data user belum berhasil dimuat', 'error');
      }
    },

    // Filter change handler
    onFilterChange() {
      // Filter will automatically update through computed property
      // but we also want to hit the API with query params when filters change
      console.log('Filters applied:', this.filters);
      if (this.debouncedFetchFilters) this.debouncedFetchFilters();
    },

    // Clear all filters
    clearFilters() {
      this.filters = { search: '', company: null };
      // reset body filters and reload immediately
      this.body.page = 1;
      this.applyFiltersToBody();
      this.loadUserMitraData();
    },

    // apply current filters to the body used for API requests
    applyFiltersToBody() {
      // keep pagination fields intact
      const page = this.body.page || 1;
      const limit = this.body.limit || 10;

      // assign 'search' and 'company' query params expected by API
      this.body = {
        ...this.body,
        page,
        limit,
        search: this.filters.search || undefined,
        company: this.filters.company || undefined
      };
    },

    // Search user by NIK (debounced)
    onSearchUser: debounce(async function(search, loading) {
      if (search.length < 3) {
        this.userOptions = [];
        return;
      }

      this.isSearchingUser = true;
      loading(true);
      
      try {
        const result = await this.searchUserByNik({ nik: search });
        
        // Keep original API structure for userOptions
        this.userOptions = result || [];
        
        console.log('Mapped userOptions:', this.userOptions);
      } catch (error) {
        this.$swal('Error', 'Pencarian user gagal', 'error');
        this.userOptions = [];
      } finally {
        this.isSearchingUser = false;
        loading(false);
      }
    }, 500),

    // When user is selected from dropdown
    onUserSelected(user) {
      if (user) {
        this.selectedUser = user;
        this.bodyAdd = {
          ...user,
          partner_role: ''
        };
      }
    },

    // Open add modal
    addData() {
      this.resetAddForm();
      this.$bvModal.show('modal-add-user-mitra');
    },

    // Reset add form
    resetAddForm() {
      this.selectedUser = null;
      this.userOptions = [];
      this.bodyAdd = {
        nik: '',
        isafe_no: '',
        fullname: '',
        email: '',
        phone: '',
        dob: '',
        doh: '',
        company: '',
        position_title: '',
        id_position: null,
        id_department: null,
        id_company: null,
        id_sub_company: null,
        photo_path: '',
        partner_role: ''
      };
      this.$nextTick(() => {
        this.errors.clear();
      });
    },

    // Save new user mitra
    async saveAdd() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$swal('Warning', 'Lengkapi semua kolom wajib', 'warning');
        return;
      }

      try {
        // Prepare body according to API specification
        const payload = {
          ...this.bodyAdd,
          partner_role: this.bodyAdd.partner_role // "user" | "adminmitra" | "adminpjo"
        };

        await this.createUserMitra(payload);
        this.$swal('Success', 'User berhasil ditambahkan', 'success');
        this.$bvModal.hide('modal-add-user-mitra');
        this.resetAddForm();
        await this.loadUserMitraData();
      } catch (error) {
        this.$swal('Error', error.message || 'User belum berhasil ditambahkan', 'error');
      }
    },

    // Open edit modal
    async editData(row) {
      try {
        // Fetch latest user data from API
        const userData = await this.getUserDetailByNik(row.nik);
        
        if (userData) {
          this.bodyUpdate = {
            id: userData.id || row.id,
            nik: userData.nik || row.nik,
            isafe_no: userData.isafe_no || '',
            full_name: userData.full_name || userData.fullname || row.full_name,
            email: userData.email || '',
            contact: userData.contact || '',
            date_of_birth: userData.date_of_birth || '',
            date_of_hire: userData.date_of_hire || '',
            company: userData.company || '',
            jabatan: userData.jabatan || userData.position || '',
            photo_path: userData.photo_path || '',
            partner_role: userData.partner_role || row.partner_role
          };
        } else {
          // Fallback to row data if API fails
          this.bodyUpdate = {
            id: row.id,
            nik: row.nik,
            isafe_no: row.isafe_no || '',
            full_name: row.full_name || row.fullname,
            email: row.email || '',
            contact: row.contact || '',
            date_of_birth: row.date_of_birth || '',
            date_of_hire: row.date_of_hire || '',
            company: row.company || '',
            jabatan: row.jabatan || row.position || '',
            photo_path: row.photo_path || '',
            partner_role: row.partner_role
          };
        }
        
        this.$bvModal.show('modal-edit-user-mitra');
      } catch (error) {
        console.error('Failed to fetch user detail:', error);
        this.$swal('Error', 'Detail user belum berhasil dimuat', 'error');
      }
    },

    // Update user mitra
    async saveUpdate() {
      const isValid = await this.$validator.validateAll();
      if (!isValid) {
        this.$swal('Warning', 'Lengkapi semua kolom wajib', 'warning');
        return;
      }

      try {
        // Update using NIK as parameter
        const payload = {
          partner_role: this.bodyUpdate.partner_role // "user" | "adminmitra" | "adminpjo"
        };
        
        await this.updateUserMitra({
          nik: this.bodyUpdate.nik,
          data: payload
        });
        this.$swal('Success', 'User berhasil diperbarui', 'success');
        this.$bvModal.hide('modal-edit-user-mitra');
        await this.loadUserMitraData();
      } catch (error) {
        this.$swal('Error', error.message || 'User belum berhasil diperbarui', 'error');
      }
    },

    // Delete user mitra
    async deleteData(nik) {
      const confirmed = await this.$bvModal.msgBoxConfirm(
        'Apakah Anda yakin ingin menghapus user ini?',
        {
          title: 'Konfirmasi Hapus',
          okVariant: 'danger',
          okTitle: 'Hapus',
          cancelTitle: 'Batal',
          centered: true
        }
      );

      if (!confirmed) return;

      try {
        // Delete using NIK as parameter
        await this.deleteUserMitra(nik);
        this.$swal('Success', 'User berhasil dihapus', 'success');
        await this.loadUserMitraData();
      } catch (error) {
        this.$swal('Error', error.message || 'User belum berhasil dihapus', 'error');
      }
    },

    // Pagination change
    onPageChange(params) {
      this.body.page = params.currentPage;
      this.loadUserMitraData();
    },

    // Per page change
    onPerPageChange(params) {
      this.body.limit = params.currentPerPage;
      this.body.page = 1; // Reset to first page
      this.loadUserMitraData();
    },

    // Format date
    formatDate(date) {
      if (!date) return '-';
      return moment(date).format('DD MMM YYYY HH:mm');
    }
  }
};
